import { isEmpty } from "lodash";
import SortBy from "PFApp/components/sort_by";
import { Card } from "PFComponents/card";
import { LoadingDots } from "PFComponents/loading_dots";
import Toggle from "PFComponents/toggle/toggle";
import { Typography } from "PFComponents/typography";
import { useMarketplaceMarkActivityAsRead } from "PFCore/hooks/queries/activities/marketplace/use_marketplace_read_mutation";
import { useInfiniteScroll } from "PFCore/hooks/use_infinite_scroll";
import { FetchActivitiesStatisticsResponse } from "PFCore/services/marketplace/fetch_activities_statistics";
import { MarketplaceActivity, MatchedActivity } from "PFTypes";
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { WorkOpportunitiesTab } from "../../../constants";
import { useMarketplaceContext } from "../../../contexts/marketplace_context";
import { useMarketplaceCounters } from "../../../hooks/use_marketplace_counters";
import { useMarketplaceFilters } from "../../../hooks/use_marketplace_filters";
import { useUnreadFilter } from "../../../hooks/use_unread_filter";
import { RoleCard } from "../../components";
import { RolesBrowserQueryData } from "../../hooks/use_roles_browser";
import { useWorkOpportunitiesTab } from "../../hooks/use_work_opportunities_tab";
import css from "./roles_browser.module.scss";

type RolesBrowserProps = RolesBrowserQueryData & {
  statistics?: FetchActivitiesStatisticsResponse;
};

export const RolesBrowser = ({
  roles,
  meta,
  fetchNextPage,
  hasNextPage,
  isLoading,
  statistics
}: RolesBrowserProps): JSX.Element => {
  const { t } = useTranslation("marketplace", { keyPrefix: "workOpportunities" });
  const { isUnreadOnly, setIsUnreadOnly } = useUnreadFilter();
  const { selectedRoleId, setSelectedRole } = useMarketplaceContext();
  const { mutate: markActivityAsRead } = useMarketplaceMarkActivityAsRead();
  const { unreadActivitiesCount } = useMarketplaceCounters();
  const { handleOrderChange } = useMarketplaceFilters(meta);

  const lastRoleRef = useInfiniteScroll<HTMLDivElement>({
    fetchMore: fetchNextPage,
    loading: isLoading,
    hasMore: hasNextPage
  });

  const currentTab = useWorkOpportunitiesTab();

  const title = {
    [WorkOpportunitiesTab.MatchingRoles]: t("matchingRoles"),
    [WorkOpportunitiesTab.Interested]: t("interested"),
    [WorkOpportunitiesTab.SavedRoles]: t("savedRoles"),
    [WorkOpportunitiesTab.SharedWithMe]: t("sharedWithMe")
  }[currentTab!];

  const selectRole = useCallback(
    (role: MatchedActivity | MarketplaceActivity) => {
      setSelectedRole(role.activity.id, role.scores);
      if (role.activity.unread) {
        const isLastUnread = currentTab === WorkOpportunitiesTab.MatchingRoles && unreadActivitiesCount === 1;
        markActivityAsRead(role.activity.id);

        if (isLastUnread) {
          setIsUnreadOnly(false);
        }
      }
    },
    [currentTab, markActivityAsRead, setIsUnreadOnly, setSelectedRole, unreadActivitiesCount]
  );

  const handleUnreadToggleChange = (val: boolean) => {
    setSelectedRole(null, null);
    setIsUnreadOnly(val);
  };

  useEffect(() => {
    if (!selectedRoleId && !isEmpty(roles) && !isLoading) {
      const firstRole = roles[0];
      // if unreadOnly is on, we select the first role, but we don't mark it as read, so it
      // stays in the collection
      isUnreadOnly ? setSelectedRole(firstRole.id, firstRole.scores) : selectRole(firstRole);
    }
  }, [selectRole, roles, selectedRoleId, isLoading, isUnreadOnly, setSelectedRole]);

  return (
    <Card className={css.card} paddingVariant="xl">
      <div className={css.header}>
        <Typography variant="h4" noMargin>
          {title}
        </Typography>
        {meta.order && (
          <SortBy
            sort={meta.order?.value?.text || ""}
            options={(meta.order?.options || []).map((option) => ({
              item: option,
              displayElement: option.text,
              id: option.text
            }))}
            onSortChange={(order) => handleOrderChange(order)}
            classes={{ selectRoot: css.sortBySelect }}
            noLabel
          />
        )}
      </div>
      {currentTab === WorkOpportunitiesTab.MatchingRoles && !!unreadActivitiesCount && (
        <Toggle
          onChange={handleUnreadToggleChange}
          description={t("rolesBrowser.showUnreadRolesOnly")}
          checked={isUnreadOnly}
        />
      )}

      <div className={css.scrollable}>
        {roles.map((role: MatchedActivity | MarketplaceActivity, index: number) => (
          <RoleCard
            role={role}
            counters={statistics?.[role.id]}
            isSelected={selectedRoleId === role.activity.id}
            ref={index === roles.length - 1 ? lastRoleRef : undefined}
            onClick={() => selectRole(role)}
            key={role.activity.id}
          />
        ))}
        {isLoading && <LoadingDots circlesEnabled circleSize="xs" />}
      </div>
    </Card>
  );
};
